import { Plural, Trans, useLingui } from "@lingui/react/macro";
import { ApiError, apiFetch } from "@pontimed/api-client";
import { createFileRoute } from "@tanstack/react-router";
import { AlertCircle, CalendarDays, Eye, Star, TrendingUp, Users } from "lucide-react";
import { useEffect, useState } from "react";

import { AccountSecurityBanner } from "@/components/account/account-security-banner";
import { useFacilityCurrency } from "@/components/account/facility/facility-currency";
import { FacilityStateCard } from "@/components/account/facility/facility-state-card";
import { PageHeader } from "@/components/layout/page-header";
import { Skeleton } from "@/components/ui/skeleton";
import { StatCard } from "@/components/ui/stat-card";
import { formatMoney } from "@/lib/format-money";

interface FacilityProfile {
  id: string;
  name: Record<string, string> | string | null;
  status: string;
  ratingAvg: string | number | null;
  reviewCount: number;
  profileViews: number | null;
}

// Shape of GET /api/account/facility/:id/financials (apps/api
// account-facility-people.ts). Flat fields; the API returns no currency, so
// it comes from the facility country (useFacilityCurrency).
interface FinancialsResponse {
  monthlyRevenue: number;
  todayCollection: number;
  outstandingAmount: number;
  outstandingCount: number;
  paidPercent: number;
}

interface PatientsResponse {
  patients: unknown[];
  kpis: {
    totalPatients: number;
    newThisMonth: number;
    returningPercent: number;
    avgVisits: number;
  };
}

interface CalendarPayload {
  providers: { id: string; name: string }[];
  events: { id: string; status: string }[];
}

export const Route = createFileRoute("/_authenticated/account/facility/$facilityId/analytics")({
  component: FacilityAnalyticsPage,
});

function FacilityAnalyticsPage() {
  const { facilityId } = Route.useParams();
  const { i18n, t } = useLingui();
  const locale = i18n.locale;
  const { currency, isLoading: currencyLoading } = useFacilityCurrency(facilityId);

  const [profile, setProfile] = useState<FacilityProfile | null>(null);
  const [financials, setFinancials] = useState<FinancialsResponse | null>(null);
  const [patients, setPatients] = useState<PatientsResponse | null>(null);
  const [calendar, setCalendar] = useState<CalendarPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<"no-access" | "load-failed" | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    const today = new Date().toISOString().slice(0, 10);
    // Each source degrades to null; remember whether any failure was NOT a
    // 403/404, so an offline page reads "couldn't load", not "no access".
    let anyLoadFailure = false;
    const soft = <T,>(promise: Promise<T>): Promise<T | null> =>
      promise.catch((err: unknown) => {
        if (err instanceof DOMException && err.name === "AbortError") throw err;
        if (!(err instanceof ApiError && (err.status === 403 || err.status === 404))) {
          anyLoadFailure = true;
        }
        return null;
      });

    Promise.all([
      // Profile is MANAGE-gated (owner/director/facility_manager/admin) but
      // legacy analytics allowed ANY facility team member — degrade to null on
      // 403/404 so receptionist/hr/billing still see the KPIs they can fetch.
      apiFetch
        .get<FacilityProfile>(`/api/account/facility/${facilityId}/profile`, {
          signal: controller.signal,
        })
        .catch((err: unknown) => {
          if (err instanceof ApiError && (err.status === 403 || err.status === 404)) return null;
          throw err;
        }),
      soft(
        apiFetch.get<FinancialsResponse>(`/api/account/facility/${facilityId}/financials`, {
          signal: controller.signal,
        }),
      ),
      soft(
        apiFetch.get<PatientsResponse>(`/api/account/facility/${facilityId}/patients`, {
          signal: controller.signal,
        }),
      ),
      soft(
        apiFetch.get<CalendarPayload>(
          `/api/account/facility/${facilityId}/calendar?date=${today}`,
          {
            signal: controller.signal,
          },
        ),
      ),
    ])
      .then(([prof, fin, pat, cal]) => {
        if (controller.signal.aborted) return;
        // Every fetch 403'd → caller is not a team member of this facility
        // (any member can at least read /patients).
        if (!prof && !fin && !pat && !cal) {
          setError(anyLoadFailure ? "load-failed" : "no-access");
          return;
        }
        setProfile(prof);
        setFinancials(fin);
        setPatients(pat);
        setCalendar(cal);
      })
      .catch((err: unknown) => {
        if (err instanceof DOMException && err.name === "AbortError") return;
        setError(
          err instanceof ApiError && (err.status === 403 || err.status === 404)
            ? "no-access"
            : "load-failed",
        );
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });

    return () => controller.abort();
  }, [facilityId, reloadKey]);

  if (loading || currencyLoading) return <AnalyticsSkeleton />;
  if (error) {
    return (
      <FacilityStateCard
        kind={error}
        facilityId={facilityId}
        onRetry={() => setReloadKey((k) => k + 1)}
      />
    );
  }

  const fmt = (amount: number) =>
    formatMoney(amount, currency, locale, { maximumFractionDigits: 0 });

  const ratingValue = profile?.ratingAvg != null ? Number(profile.ratingAvg) : null;
  const reviewCount = profile?.reviewCount ?? 0;
  const ONE_DECIMAL = { minimumFractionDigits: 1, maximumFractionDigits: 1 };
  const PERCENT = { style: "percent", maximumFractionDigits: 0 } as const;
  const todayCount = calendar?.events.filter((e) => e.status !== "cancelled").length ?? null;
  const activeProvidersCount = calendar?.providers.length ?? null;

  return (
    <div className="space-y-6">
      <PageHeader
        title={<Trans>Analytics</Trans>}
        description={<Trans>Performance, patients, and revenue at a glance.</Trans>}
      />

      {/* Top KPI cards */}
      {/* Monthly-appointments KPI (legacy analytics) intentionally skipped:
          no existing endpoint returns a month count cheaply (calendar is
          per-day, dashboard is today + 7-day). Revisit in V1.1. */}
      <section>
        <h2 className="text-foreground mb-3 text-sm font-semibold">
          <Trans>This month</Trans>
        </h2>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <StatCard
            label={t`Revenue`}
            value={financials ? fmt(financials.monthlyRevenue) : "—"}
            icon={TrendingUp}
            tone="success"
          />
          <StatCard
            label={t`New patients`}
            value={patients ? patients.kpis.newThisMonth : "—"}
            icon={Users}
            tone="primary"
          />
          <StatCard
            label={t`Returning`}
            value={patients ? patients.kpis.returningPercent / 100 : "—"}
            format={PERCENT}
            icon={CalendarDays}
            tone="accent"
          />
          <StatCard
            label={t`Collection rate`}
            value={financials ? financials.paidPercent / 100 : "—"}
            format={PERCENT}
            icon={TrendingUp}
            tone="warning"
          />
        </div>
      </section>

      {/* Quality + reach — four cards (rating and review count share one) so
          the 4-column row has no orphan. */}
      <section>
        <h2 className="text-foreground mb-3 text-sm font-semibold">
          <Trans>Quality &amp; reach</Trans>
        </h2>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <StatCard
            label={t`Profile views`}
            value={profile ? (profile.profileViews ?? 0) : "—"}
            icon={Eye}
            tone="primary"
          />
          <StatCard
            label={t`Rating`}
            value={ratingValue ?? "—"}
            format={ONE_DECIMAL}
            icon={Star}
            tone="warning"
            hint={
              profile ? <Plural value={reviewCount} one="# review" other="# reviews" /> : undefined
            }
          />
          <StatCard
            label={t`Total patients`}
            value={patients ? patients.kpis.totalPatients : "—"}
            icon={Users}
            tone="primary"
          />
          <StatCard
            label={t`Avg visits`}
            value={patients ? patients.kpis.avgVisits : "—"}
            format={ONE_DECIMAL}
            icon={Users}
            tone="accent"
          />
        </div>
      </section>

      {/* Today */}
      <section>
        <h2 className="text-foreground mb-3 text-sm font-semibold">
          <Trans>Today</Trans>
        </h2>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <StatCard
            label={t`Appointments`}
            value={todayCount ?? "—"}
            icon={CalendarDays}
            tone="primary"
          />
          <StatCard
            label={t`Revenue today`}
            value={financials ? fmt(financials.todayCollection) : "—"}
            icon={TrendingUp}
            tone="success"
          />
          <StatCard
            label={t`Active providers`}
            value={activeProvidersCount ?? "—"}
            icon={Users}
            tone="accent"
          />
          <StatCard
            label={t`Outstanding`}
            value={financials ? fmt(financials.outstandingAmount) : "—"}
            icon={AlertCircle}
            tone="warning"
          />
        </div>
      </section>

      <p className="text-muted-foreground text-center text-xs">
        <Trans>Charts (last 7 / 30 days appointments + popular hours) ship V1.1.</Trans>
      </p>

      <AccountSecurityBanner />
    </div>
  );
}

function AnalyticsSkeleton() {
  return (
    <div className="space-y-6">
      <Skeleton className="h-10 w-48" />
      {[0, 1, 2].map((i) => (
        <div key={i} className="space-y-3">
          <Skeleton className="h-5 w-32" />
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            {Array.from({ length: 4 }).map((_, j) => (
              <Skeleton key={j} className="h-20 rounded-xl" />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
